import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Layout, PageHeader, Container } from '../components/layout/Layout';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Plus, ExternalLink, Trash2, Calendar, Users, Eye, Clock } from 'lucide-react';

interface SchedulingPage {
  id: string;
  title: string;
  slug: string;
  description?: string;
  duration: number;
  isActive: boolean;
  viewCount: number;
  projectId?: string;
  _count: {
    bookings: number;
  };
}

export default function SchedulingList() {
  const navigate = useNavigate();
  const [pages, setPages] = useState<SchedulingPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [projectMap, setProjectMap] = useState<Record<string, string>>({});

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    try {
      setLoading(true);
      const [pagesData, projectsData] = await Promise.allSettled([
        api.getSchedulingPages() as Promise<{ pages: SchedulingPage[] }>,
        api.getEurekaProjects(),
      ]);

      if (pagesData.status === 'fulfilled') {
        setPages(pagesData.value.pages);
      }
      if (projectsData.status === 'fulfilled') {
        const map: Record<string, string> = {};
        for (const p of projectsData.value.projects) {
          map[p.id] = p.name;
        }
        setProjectMap(map);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this scheduling page?')) return;
    
    try {
      await api.deleteSchedulingPage(id);
      setPages(pages.filter(p => p.id !== id));
    } catch (err: any) {
      setError(err.message);
    }
  };

  const getPublicUrl = (slug: string) => {
    return `${window.location.origin}/schedule/${slug}`;
  };

  const totalBookings = pages.reduce((acc, p) => acc + p._count.bookings, 0);
  const totalViews = pages.reduce((acc, p) => acc + p.viewCount, 0);

  return (
    <Layout>
      <PageHeader
        title="Scheduling Pages"
        description="Manage your booking pages and view appointments"
        action={
          <Button
            onClick={() => navigate('/scheduling/new')}
            leftIcon={<Plus className="w-4 h-4" />}
          >
            Create New Page
          </Button>
        }
      />

      <Container>
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
            {error}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-blue-500 rounded-xl">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Total Pages</p>
                  <p className="text-2xl font-bold text-gray-900">{pages.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-100">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-green-500 rounded-xl">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Total Bookings</p>
                  <p className="text-2xl font-bold text-gray-900">{totalBookings}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-100">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-purple-500 rounded-xl">
                  <Eye className="w-6 h-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Total Views</p>
                  <p className="text-2xl font-bold text-gray-900">{totalViews}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : pages.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-gray-400" />
              </div>
              <CardTitle className="text-xl mb-2">No scheduling pages yet</CardTitle>
              <CardDescription className="mb-6 max-w-md mx-auto">
                Create your first scheduling page to start accepting bookings from others
              </CardDescription>
              <Button
                onClick={() => navigate('/scheduling/new')}
                leftIcon={<Plus className="w-4 h-4" />}
              >
                Create Your First Page
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {pages.map((page) => (
              <Card key={page.id} hover className="group">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="group-hover:text-blue-600 transition-colors">
                        {page.title}
                      </CardTitle>
                      {page.projectId && projectMap[page.projectId] && (
                        <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                          {projectMap[page.projectId]}
                        </span>
                      )}
                      {page.description && (
                        <CardDescription className="mt-1 line-clamp-2">
                          {page.description}
                        </CardDescription>
                      )}
                    </div>
                    <Badge variant={page.isActive ? 'success' : 'default'}>
                      {page.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </CardHeader>

                <CardContent>
                  <div className="flex items-center text-sm text-gray-600 mb-4">
                    <Clock className="w-4 h-4 mr-2" />
                    {page.duration} minutes
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-xl p-3 text-center">
                      <p className="text-2xl font-bold text-gray-900">{page.viewCount}</p>
                      <p className="text-xs text-gray-500">Views</p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3 text-center">
                      <p className="text-2xl font-bold text-gray-900">{page._count.bookings}</p>
                      <p className="text-xs text-gray-500">Bookings</p>
                    </div>
                  </div>
                </CardContent>

                <CardFooter className="flex items-center justify-between">
                  <div className="flex space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/scheduling/${page.id}/edit`)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(page.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <a
                    href={getPublicUrl(page.slug)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-700"
                  >
                    View
                    <ExternalLink className="w-4 h-4 ml-1" />
                  </a>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </Container>
    </Layout>
  );
}
